import React, { useState } from 'react';
import { useData } from '../../hooks';
import { useRTL } from 'ca-hooks';
import { Icon } from '@kaizen/component-library';
import { ConfirmationModal } from '@kaizen/components';
import styles from './WelcomeScreenSettings.scss';
import { WelcomeScreenSettingsRow } from '../';
import SettingsIcon from '@kaizen/component-library/icons/settings.icon.svg';
import SecurityIcon from '@kaizen/component-library/icons/security-tip.icon.svg';
import UserIcon from '@kaizen/component-library/icons/user.icon.svg';
import VisibleIcon from '@kaizen/component-library/icons/visible.icon.svg';
import {
  DataSafetyPolicyViewed,
  OutboundDestinationVisited,
  SpecificSurveyRulesViewed,
} from '../../../../../../ampli';
import { sendAmpliEvent } from '../../../../../../ampli/sendAmpliEvent';
import { FormattedMessage, useIntl } from '@cultureamp/i18n-react-intl';

interface ModalContent {
  title: string | null;
  content: string | null;
}

export const WelcomeScreenSettings = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<ModalContent>({
    title: null,
    content: null,
  });
  const { formatMessage } = useIntl();

  const {
    aggregate_id,
    privacy_policy,
    confidentiality_content,
    product_type,
    threesixty_name,
    account_name,
    raw_data_extract_warning,
    welcome_screen_locale,
  } = useData();

  const isRTL = useRTL(welcome_screen_locale.code);

  const replaceLinkTarget = (text: string) => {
    const modalContentReplaced = text.replaceAll('<a', '<a target="_blank"');
    return modalContentReplaced;
  };

  const showConfidentialityModal = async () => {
    setShowModal(true);
    setModalContent({
      title: confidentiality_content.subject,
      content: replaceLinkTarget(confidentiality_content.message),
    });
    const event = new SpecificSurveyRulesViewed();
    sendAmpliEvent({ ...event, group_properties: { Survey: aggregate_id } });
  };

  const showDataSafetyModal = () => {
    setShowModal(true);
    setModalContent({
      title: formatMessage({
        id: 'welcome_screen.data_safety_policy_title',
        defaultMessage: 'Data safety policy',
      }),
      content: replaceLinkTarget(privacy_policy),
    });
    const event = new DataSafetyPolicyViewed();
    sendAmpliEvent({ ...event, group_properties: { Survey: aggregate_id } });
  };

  const sendAmplitudeTrackingForPrivacyPolicy = () => {
    const event = new OutboundDestinationVisited({
      'Destination domain': 'cultureamp.com',
      'Element clicked name': 'Privacy Policy',
      Context: 'Survey Welcome Page',
      'Custom content enabled': undefined,
      'Site hierarchy - L1': '',
    });
    sendAmpliEvent(event);
  };

  return (
    <>
      <div className={styles.WelcomeScreenSettingsContainer}>
        {product_type === 'effectiveness' && threesixty_name !== '' && (
          <WelcomeScreenSettingsRow
            icon={
              <Icon icon={UserIcon} inheritSize={true} role="presentation" />
            }
            direction={isRTL ? 'rtl' : 'ltr'}
          >
            <FormattedMessage
              id="welcome_screen.three_sixty_feedback_for"
              defaultMessage="You have been asked to provide feedback for {threesixty_name}."
              values={{ threesixty_name }}
            />
          </WelcomeScreenSettingsRow>
        )}
        {raw_data_extract_warning && (
          <WelcomeScreenSettingsRow
            icon={
              <Icon icon={VisibleIcon} inheritSize={true} role="presentation" />
            }
            direction={isRTL ? 'rtl' : 'ltr'}
          >
            <div
              className={styles.RawDataExtractMessage}
              dangerouslySetInnerHTML={{
                __html: replaceLinkTarget(raw_data_extract_warning),
              }}
            />
          </WelcomeScreenSettingsRow>
        )}
        <WelcomeScreenSettingsRow
          icon={
            <Icon icon={SettingsIcon} inheritSize={true} role="presentation" />
          }
          direction={isRTL ? 'rtl' : 'ltr'}
        >
          <FormattedMessage
            id="welcome_screen.survey_settings"
            defaultMessage="The <button>settings for this survey</button> control how your responses can be used by {account_name}."
            values={{
              button: (chunks) => (
                <button
                  className={styles.WelcomeScreenSettingsButton}
                  onClick={() => showConfidentialityModal()}
                >
                  {chunks}
                </button>
              ),
              account_name,
            }}
          />
        </WelcomeScreenSettingsRow>
        <WelcomeScreenSettingsRow
          icon={
            <Icon icon={SecurityIcon} inheritSize={true} role="presentation" />
          }
          direction={isRTL ? 'rtl' : 'ltr'}
        >
          <FormattedMessage
            id="welcome_screen.survey_privacy"
            defaultMessage="Your information will be stored and processed in accordance with Culture Amp’s <a>Privacy Policy</a>. <button>More on managing information</button>."
            values={{
              button: (chunks) => (
                <button
                  onClick={() => showDataSafetyModal()}
                  className={styles.WelcomeScreenSettingsButton}
                >
                  {chunks}
                </button>
              ),
              a: (chunks) => (
                <a
                  className={styles.settingsLink}
                  target="_blank"
                  onClick={sendAmplitudeTrackingForPrivacyPolicy}
                  href="https://www.cultureamp.com/privacy-policy"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </WelcomeScreenSettingsRow>
      </div>
      <ConfirmationModal
        isOpen={showModal}
        mood="informative"
        title={modalContent.title ? modalContent.title : ''}
        onDismiss={() => setShowModal(false)}
        dismissLabel={formatMessage({
          id: 'welcome_screen.modal_done',
          defaultMessage: 'Done',
        })}
        automationId="welcome-settings-modal"
      >
        <div className={styles.messageContainer}>
          <div
            className={styles.MessageHTMLContent}
            tabIndex={0}
            dangerouslySetInnerHTML={{
              __html: modalContent.content ? modalContent.content : '',
            }}
          />
        </div>
      </ConfirmationModal>
    </>
  );
};
